/**
 * @file api.ts
 * @brief TanStack Query hooks for company-scoped invoices.
 *
 * @author DES
 * @date 19-06-2026
 */

import {
  useQueries,
  useQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { z } from "zod";
import { apiGet } from "@/shared/api/client";
import { invoiceEndpoints } from "@/shared/api/endpoints";
import {
  CompanyInvoicesResponseSchema,
  invoicePeriodRange,
  type CompanyInvoicesListParams,
  type CompanyInvoicesResponse,
} from "./schemas";

export const companyInvoicesKeys = {
  all: ["company", "invoices"] as const,
  list: (params: CompanyInvoicesListParams) =>
    ["company", "invoices", "list", params] as const,
};

function buildQuery(params: CompanyInvoicesListParams): string {
  const usp = new URLSearchParams();
  usp.set("page", String(params.page ?? 1));
  usp.set("pageSize", String(params.pageSize ?? 25));
  if (params.q) usp.set("q", params.q);
  if (params.employee_id) usp.set("employee_id", params.employee_id);

  if (params.from) {
    usp.set("from", params.from);
  } else if (params.period && params.period !== "all") {
    const range = invoicePeriodRange(params.period);
    if (range.from) usp.set("from", range.from);
  }

  if (params.to) {
    usp.set("to", params.to);
  } else if (params.period && params.period !== "all") {
    const range = invoicePeriodRange(params.period);
    if (range.to) usp.set("to", range.to);
  }

  return usp.toString();
}

/** One owner in the grouped list (`?group=employee`, 01-10-2026). */
export const InvoicesEmployeeSummarySchema = z.object({
  owner_id: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  avatar_url: z.string().nullable(),
  count: z.number(),
});
export type InvoicesEmployeeSummary = z.infer<typeof InvoicesEmployeeSummarySchema>;

/** The group key for an owner; `none` for invoices with no owner. */
export const invoiceOwnerGroupKey = (ownerId: string | null) => ownerId ?? "none";

/** The list filters without paging or employee (shared by the grouped calls). */
function filterQuery(params: CompanyInvoicesListParams): URLSearchParams {
  const usp = new URLSearchParams(buildQuery({ ...params, employee_id: undefined }));
  usp.delete("page");
  usp.delete("pageSize");
  return usp;
}

/**
 * Every employee with invoices under the current filters, with counts
 * (DES 01-10-2026: "is page per bhi whoi, pagination ki waja say masla hai
 * users group wala"), as on Sessions. One server GROUP BY.
 */
export function useCompanyInvoicesEmployees(params: CompanyInvoicesListParams, enabled: boolean) {
  return useQuery({
    queryKey: [...companyInvoicesKeys.all, "employees", filterQuery(params).toString()],
    enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const usp = filterQuery(params);
      usp.set("group", "employee");
      const raw = await apiGet<{ employees?: unknown }>(`${invoiceEndpoints.list}?${usp}`);
      return z.array(InvoicesEmployeeSummarySchema).parse(raw.employees ?? []);
    },
  });
}

/** Rows per batch when a group opens or loads more. */
export const INVOICE_GROUP_BATCH = 25;

/**
 * The invoices of each OPEN group, `batches` pages of INVOICE_GROUP_BATCH,
 * under the same filters (`?owner=`). The server caps `pageSize` at 100, so
 * the pages are fetched and joined rather than asking for one big page.
 */
export function useCompanyInvoicesGroupRows(
  params: CompanyInvoicesListParams,
  open: { key: string; batches: number }[],
) {
  return useQueries({
    queries: open.map(({ key, batches }) => ({
      queryKey: [
        ...companyInvoicesKeys.all,
        "group",
        key,
        batches,
        filterQuery(params).toString(),
      ],
      placeholderData: (prev: CompanyInvoicesResponse | undefined) => prev,
      queryFn: async (): Promise<CompanyInvoicesResponse> => {
        const pages = await Promise.all(
          Array.from({ length: batches }, async (_, i) => {
            const usp = filterQuery(params);
            usp.set("owner", key);
            usp.set("page", String(i + 1));
            usp.set("pageSize", String(INVOICE_GROUP_BATCH));
            const raw = await apiGet<unknown>(`${invoiceEndpoints.list}?${usp}`);
            return CompanyInvoicesResponseSchema.parse(raw);
          }),
        );
        return { ...pages[0], items: pages.flatMap((p) => p.items ?? []) };
      },
    })),
  });
}

export function useCompanyInvoicesList(
  params: CompanyInvoicesListParams,
  options?: Partial<UseQueryOptions<CompanyInvoicesResponse>>,
) {
  return useQuery<CompanyInvoicesResponse>({
    queryKey: companyInvoicesKeys.list(params),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `${invoiceEndpoints.list}?${buildQuery(params)}`,
      );
      return CompanyInvoicesResponseSchema.parse(raw);
    },
    placeholderData: (prev) => prev,
    ...options,
  });
}

export function companyInvoicesExportUrl(
  params: Pick<
    CompanyInvoicesListParams,
    "from" | "to" | "employee_id" | "period"
  >,
): string {
  const usp = new URLSearchParams();
  if (params.employee_id) usp.set("employee_id", params.employee_id);
  if (params.from) {
    usp.set("from", params.from);
  } else if (params.period && params.period !== "all") {
    const range = invoicePeriodRange(params.period);
    if (range.from) usp.set("from", range.from);
  }
  if (params.to) {
    usp.set("to", params.to);
  } else if (params.period && params.period !== "all") {
    const range = invoicePeriodRange(params.period);
    if (range.to) usp.set("to", range.to);
  }
  const qs = usp.toString();
  return qs
    ? `${invoiceEndpoints.export}?${qs}`
    : invoiceEndpoints.export;
}
